import { Component, computed, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { Comment, Upload, PipesModule, normalizeCommentTypeColor } from '@xmedical/core';
import { XmedicalUiModule } from '@xmedical/ui';
import { PlaybackComponent } from '@xmedical/transcribe';
import { WithReferralInfo, hasReferralInfo, formatReferralSource } from '../../types/referral-info';

/**
 * Extended Comment interface that includes referral info.
 * The base Comment from xmedical-core will have these properties added from the API.
 */
export type CommentWithReferralInfo = Comment & WithReferralInfo;

/**
 * Event emitted when a download is requested for an attachment.
 */
export interface ClinicalNoteDownloadRequestedEvent {
  upload: Upload;
}

@Component({
  selector: 'xm-clinical-notes-widget-view',
  templateUrl: './clinical-notes-widget-view.component.html',
  styleUrls: ['./clinical-notes-widget-view.component.scss'],
  imports: [
    DatePipe,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatIconModule,
    MatListModule,
    XmedicalUiModule,
    PipesModule,
    PlaybackComponent
  ]
})
export class ClinicalNotesWidgetViewComponent {
  // Inputs
  comments = input<Comment[]>([]);
  title = input<string>('Clinical Notes');
  isLoading = input<boolean>(false);
  usePrimaryText = input<boolean>(false);
  displayTypeBadge = input<boolean>(false);
  // Rollback-to-draft inputs. Parent supplies practice settings + the current
  // user id; this dumb component decides which comments are eligible.
  currentUserId = input<string>('');
  rollbackEnabled = input<boolean>(false);
  rollbackWindowMinutes = input<number>(0);

  // Outputs
  downloadRequested = output<ClinicalNoteDownloadRequestedEvent>();
  unlockRequested = output<{commentId: number}>();

  // Computed signals
  hasComments = computed(() => this.comments().length > 0);
  sortedComments = computed(() =>
    [...this.comments()].sort((a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    )
  );

  onDownloadAttachment(upload: Upload): void {
    this.downloadRequested.emit({ upload });
  }

  hasTranscriptions(comment: Comment): boolean {
    return comment.transcriptions && comment.transcriptions.length > 0;
  }

  hasUploads(comment: Comment): boolean {
    return comment.uploads && comment.uploads.length > 0;
  }

  hasContent(comment: Comment): boolean {
    return !!(comment.comment?.text && comment.comment.text.trim().length > 0);
  }

  // Referral helper methods for template
  isReferred(comment: CommentWithReferralInfo): boolean {
    return hasReferralInfo(comment);
  }

  getReferralText(comment: CommentWithReferralInfo): string {
    return formatReferralSource(comment);
  }

  getCommentCardBackgroundColor(comment: Comment): string | null {
    return normalizeCommentTypeColor(comment.commentType?.color);
  }

  canUnlock(comment: Comment): boolean {
    if (!this.rollbackEnabled()) return false;
    if (!comment.locked || !comment.lockedAt) return false;
    if (comment.lastModifiedUser?.userId !== this.currentUserId()) return false;
    const lockedAtMs = new Date(comment.lockedAt).getTime();
    if (Number.isNaN(lockedAtMs)) return false;
    const elapsedMs = Date.now() - lockedAtMs;
    const windowMs = this.rollbackWindowMinutes() * 60_000;
    return elapsedMs >= 0 && elapsedMs <= windowMs;
  }

  onUnlockClicked(comment: Comment): void {
    this.unlockRequested.emit({commentId: comment.id});
  }
}
